import { getLandlordUsers } from '@/services/appService';
import {
  getApplications,
  getRemovedApplications,
  abortApplication,
  approveApplication,
  restoreApplication,
  rejectApplication,
  removeApplication,
  cancelDeleteApplication
} from '@/services/baService';
import {
  getAhApplications,
  getArchivedAhApplications,
  approveAhApplication,
  restoreAhApplication,
  rejectAhApplication,
  removeAhApplication,
  closeAhApplication
} from '@/services/ahService';
import {
  getOvApplications,
  rejectOvApplication,
  removeOvApplication,
  abortOvApplication,
  approveOvApplication,
  closeOvApplication,
  getArchivedOvApplications,
  setOvTransferDate
} from '@/services/ovService';
import statusTypes from '@/utils/statusTypes';

async function fetchApps(ctx) {
  ctx.commit('setAppsLoading', true);
  try {
    const res = await getApplications();
    ctx.commit('setApps', res);
  } catch {
    console.error('Failed to fetch applications');
  }
  ctx.commit('setAppsLoading', false);
}

async function fetchSubletApps(ctx) {
  ctx.commit('setAppsLoading', true);
  let applications = (await getAhApplications()) || [];

  ctx.commit('mergeAhApps', applications);

  const landlordMap = new Map();
  applications.forEach(app => {
    if (app.landlordId && !landlordMap.has(app.landlordId)) {
      landlordMap.set(app.landlordId, {
        id: app.landlordId,
        name: app.landlordName
      });
    }
  });

  const landlords = Array.from(landlordMap.values());

  ctx.commit('setLandlords', landlords);
  ctx.commit('setAppsLoading', false);
}

async function fetchRemovedApps(ctx) {
  const res = await getRemovedApplications();
  ctx.commit('setRemovedApps', res);
}

async function fetchAhArchivedApps(ctx) {
  ctx.commit('setAppsLoading', true);
  const res = await getArchivedAhApplications();
  ctx.commit('mergeAhApps', res);
  ctx.commit('setAppsLoading', false);
}

async function abortApp(ctx, { id }) {
  try {
    await abortApplication(id);

    ctx.commit('setApplicationStatus', {
      id,
      status: statusTypes.ABORTED
    });
  } catch {
    console.error('Failed to ABORT application');
  }
}

async function approveApp(ctx, { id }) {
  try {
    await approveApplication(id);

    ctx.commit('setApplicationStatus', {
      id,
      status: statusTypes.APPROVED
    });
  } catch {
    console.error('Failed to APPROVE application');
  }
}

async function restoreApp(ctx, id) {
  try {
    await restoreApplication(id);

    ctx.commit('setApplicationStatus', {
      id,
      status: statusTypes.READY
    });
  } catch {
    console.error('Failed to RESTORE application');
  }
}

async function approveAhApp(ctx, { id, startDate, endDate }) {
  try {
    await approveAhApplication(id, { startDate, endDate });
    ctx.commit('setAhApplicationDates', {
      id,
      startDate,
      endDate
    });
    ctx.commit('setAhApplicationStatus', {
      id,
      status: statusTypes.APPROVED
    });
  } catch {
    console.error('Failed to APPROVE application');
  }
}

async function restoreAhApp(ctx, { id }) {
  try {
    await restoreAhApplication(id);

    ctx.commit('setAhApplicationStatus', {
      id,
      status: statusTypes.READY
    });
  } catch {
    console.error('Failed to RESTORE ah application');
  }
}

async function rejectApp(ctx, { id, note }) {
  try {
    await rejectApplication(id, note);

    ctx.commit('setApplicationStatus', {
      id,
      status: statusTypes.REJECTED
    });
  } catch {
    console.error('Failed to REJECT application');
  }
}

async function rejectAhApp(ctx, { id, note }) {
  try {
    await rejectAhApplication(id, note);

    ctx.commit('setAhApplicationStatus', {
      id,
      status: statusTypes.REJECTED
    });
  } catch {
    console.error('Failed to REJECT application');
  }
}

async function fetchLandlordUsersWithUnassigned(ctx, applicationId) {
  try {
    const activeApp = ctx.rootState.app.activeApp;
    let users = await getLandlordUsers(applicationId, activeApp);
    const unassignedUser = {
      firstName: 'Ej',
      lastName: 'Tilldelad',
      fullName: 'Ej Tilldelad',
      id: null,
      selected: false
    };
    users = users
      .map(user => ({
        ...user,
        fullName: user.firstName + ' ' + user.lastName,
        selected: false
      }))
      .sort((a, b) => a.fullName.localeCompare(b.fullName, 'sv'));
    ctx.commit('setLandlordUsers', [unassignedUser, ...users]);
  } catch {
    console.error('Failed to fetch landlord users');
  }
}

async function removeApp(ctx, { id, note }) {
  try {
    await removeApplication(id, note);

    ctx.commit('setApplicationStatus', {
      id,
      status: 'REMOVED'
    });
  } catch {
    console.error('Failed to REMOVE application');
  }
}

async function removeAhApp(ctx, { id, note }) {
  try {
    await removeAhApplication(id, note);

    ctx.commit('setAhApplicationStatus', {
      id,
      status: 'REMOVED'
    });
  } catch {
    console.error('Failed to REMOVE application');
  }
}

async function cancelRemoveApp(ctx, id) {
  try {
    const prevStatus = await cancelDeleteApplication(id);

    ctx.commit('setApplicationStatus', {
      id,
      status: prevStatus
    });
  } catch {
    console.error('Failed to CANCEL REMOVE application');
  }
}

async function clearFilter({ commit, state, dispatch }) {
  commit('resetFilterTags');
  commit(
    'setFilterCategories',
    state.filterCategories?.map(status => ({
      ...status,
      selected: false
    }))
  );
  commit(
    'setSubletFilterCategories',
    state.subletFilterCategories?.map(status => ({
      ...status,
      selected: false
    }))
  );
  commit(
    'setOvFilterCategories',
    state.ovFilterCategories?.map(status => ({
      ...status,
      selected: false
    }))
  );
  commit(
    'setLandlords',
    state.landlords?.map(landlord => ({
      ...landlord,
      selected: false
    }))
  );
  dispatch('resetSelectedLandlordUsers');
  commit('setDatePickerValue', {
    type: 'newCaseFromDate',
    value: null
  });
  commit('setDatePickerValue', {
    type: 'newCaseToDate',
    value: null
  });
  commit('setDatePickerValue', {
    type: 'swapFromDate',
    value: null
  });
  commit('setDatePickerValue', {
    type: 'swapToDate',
    value: null
  });
}

async function resetSelectedLandlordUsers({ commit, state }) {
  commit(
    'setLandlordUsers',
    state.landlordUsers?.map(user => ({
      ...user,
      selected: false
    }))
  );
}

async function closeAhApp(ctx, { id }) {
  try {
    await closeAhApplication(id);

    ctx.commit('setAhApplicationStatus', {
      id,
      status: statusTypes.CLOSED
    });
  } catch {
    console.error('Failed to CLOSE ah application');
  }
}

async function fetchOvApps(ctx) {
  ctx.commit('setAppsLoading', true);
  const applicationType = ctx.rootGetters['app/activeOvApplicationType'];
  const res = await getOvApplications(applicationType);
  ctx.commit('mergeOvApps', res);
  ctx.commit('setAppsLoading', false);
}

async function rejectOvApp(ctx, { id, note }) {
  try {
    await rejectOvApplication(id, note);

    ctx.commit('setOvApplicationStatus', {
      id,
      status: statusTypes.REJECTED
    });
  } catch {
    console.error('Failed to REJECT application');
  }
}

async function removeOvApp(ctx, { id, note }) {
  try {
    await removeOvApplication(id, note);

    ctx.commit('setOvApplicationStatus', {
      id,
      status: 'REMOVED'
    });
  } catch {
    console.error('Failed to REMOVE ov application');
  }
}

async function abortOvApp(ctx, { id, note }) {
  try {
    await abortOvApplication(id, note);

    ctx.commit('setOvApplicationStatus', {
      id,
      status: statusTypes.ABORTED
    });
  } catch {
    console.error('Failed to ABORT ov application');
  }
}

async function approveOvApp(ctx, { id }) {
  try {
    await approveOvApplication(id);

    ctx.commit('setOvApplicationStatus', {
      id,
      status: statusTypes.APPROVED
    });
  } catch {
    console.error('Failed to APPROVE application');
  }
}

async function closeOvApp(ctx, { id }) {
  try {
    await closeOvApplication(id);

    ctx.commit('setOvApplicationStatus', {
      id,
      status: statusTypes.CLOSED
    });
  } catch {
    console.error('Failed to CLOSE ov application');
  }
}

async function fetchOvArchivedApps(ctx) {
  ctx.commit('setAppsLoading', true);
  const res = await getArchivedOvApplications();
  ctx.commit('mergeOvApps', res);
  ctx.commit('setAppsLoading', false);
}

async function setTransferDate(ctx, { id, actualTransferDate }) {
  await setOvTransferDate(id, actualTransferDate);

  ctx.commit('setOvTransferDate', {
    id,
    actualTransferDate
  });
}

export default {
  fetchApps,
  fetchSubletApps,
  fetchRemovedApps,
  fetchAhArchivedApps,
  approveApp,
  restoreApp,
  approveAhApp,
  restoreAhApp,
  rejectApp,
  rejectAhApp,
  fetchLandlordUsersWithUnassigned,
  abortApp,
  removeApp,
  removeAhApp,
  cancelRemoveApp,
  clearFilter,
  closeAhApp,
  fetchOvApps,
  rejectOvApp,
  removeOvApp,
  abortOvApp,
  approveOvApp,
  closeOvApp,
  fetchOvArchivedApps,
  setTransferDate,
  resetSelectedLandlordUsers
};
